/**
 * DeleteConfirmation — inline confirmation block shown inside the flyout
 * drawer when the user clicks "Delete".
 *
 * Extracted from V2Users, V2Roles, V2Connectors, and V2SummaryTemplates
 * to eliminate ~35 duplicated lines per page.
 */

import { Box } from "@mui/material";
import { confirmCancelBtnSx, confirmDeleteBtnSx } from "./crudStyles";

interface DeleteConfirmationProps {
  /** Confirmation message (e.g. "Are you sure you want to delete 'Admin'?") */
  readonly message: string;
  /** Error message from a failed delete attempt */
  readonly error?: string;
  readonly onCancel: () => void;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly confirmLabel: string;
  readonly isPending?: boolean;
  readonly pendingLabel?: string;
}

export default function DeleteConfirmation({
  message,
  error,
  onCancel,
  cancelLabel,
  onConfirm,
  confirmLabel,
  isPending = false,
  pendingLabel,
}: DeleteConfirmationProps) {
  return (
    <Box
      sx={{
        borderTop: "1px solid var(--border-subtle)",
        pt: "16px",
        mt: "4px",
      }}
    >
      <Box sx={{ color: "var(--text-secondary)", fontSize: "0.875rem", lineHeight: 1.6, mb: "12px" }}>
        {message}
      </Box>
      {error && (
        <Box sx={{ color: "#FB7185", fontSize: "0.8125rem", mb: 1 }}>
          {error}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: "8px" }}>
        <Box
          component="button"
          onClick={onCancel}
          sx={confirmCancelBtnSx}
        >
          {cancelLabel}
        </Box>
        <Box
          component="button"
          onClick={onConfirm}
          disabled={isPending}
          sx={confirmDeleteBtnSx}
        >
          {isPending && pendingLabel ? pendingLabel : confirmLabel}
        </Box>
      </Box>
    </Box>
  );
}
